import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/api";
import { User, CalendarDays, ClipboardCheck, Star, TrendingUp, Wallet, Megaphone, LogOut, Menu, X, UserCog, CalendarCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import OneSignalProvider from "@/components/OneSignalProvider";
import AlertManager from "@/components/AlertManager";

const items = [
  { to: "/aluno",             label: "Meu Perfil",   icon: User, end: true },
  { to: "/aluno/perfil",      label: "Editar Perfil", icon: UserCog },
  { to: "/aluno/turma",       label: "Minha Turma",  icon: CalendarDays },
  { to: "/aluno/calendario",  label: "Calendário",   icon: CalendarCheck2 },
  { to: "/aluno/presenca",    label: "Presença",     icon: ClipboardCheck },
  { to: "/aluno/avaliacoes",  label: "Avaliações",   icon: Star },
  { to: "/aluno/evolucao",    label: "Evolução",     icon: TrendingUp },
  { to: "/aluno/financeiro",  label: "Financeiro",   icon: Wallet },
  { to: "/aluno/avisos",      label: "Avisos",       icon: Megaphone },
];

export default function StudentLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <OneSignalProvider studentId={user?.student_id}>
    <div className="min-h-screen flex">
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-card border-r border-border transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="student-sidebar"
      >
        <div className="h-24 px-5 flex items-center gap-3 border-b border-border">
          <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-16 w-auto vv-logo-glow" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Painel</div>
            <div className="font-bold text-lg vv-gradient-text">Aluno</div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)}>
            <X className="size-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-primary/10 text-foreground border border-primary/30"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <Icon className="size-4 text-[hsl(var(--vv-lime))]" />
              <span className="font-semibold tracking-wide uppercase text-xs">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-border bg-card">
          <div className="flex items-center gap-3 mb-3">
            <img src={BRAND.MANGUITA_FORCA} alt="" className="size-10 rounded-full object-cover object-top bg-primary/10" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="size-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-card border-b border-border h-14 flex items-center px-4 gap-3">
          <button onClick={() => setOpen(true)}><Menu className="size-6" /></button>
          <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-10" />
          <span className="font-bold vv-gradient-text">Vôlei Vida</span>
        </header>
        <main className="p-5 lg:p-8 max-w-[1400px] mx-auto vv-fade-in">
          <Outlet />
          <AlertManager />
        </main>
      </div>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
    </OneSignalProvider>
  );
}
